import { useTrackContext } from "../../hooks/useTrackContext";
import { AiOutlineCheckCircle, AiOutlineClose } from "react-icons/ai";
import React, { useMemo, useState } from "react";
import { IGVariant, TGameCustomization, TSelectedVariantAndDate, TSelectedVariants } from "../../types";
import { handleAddToCartClick } from "../../utils/cartOperations";

const Notification: React.FC = () => {
  const {
    notification,
    dispatch,
    trackSelectionOption,
    mainTrackSelection,
    miniTrackSection,
    juniorTrackSelection,
    vrSelection,
    metaObjectMainTrackSectionConfig,
    metaObjectMiniTrackSectionConfig,
    metaObjectJuniorTrackSectionConfig,
    metaObjectVRSectionConfig,
    metaObjectBattleKartTrackSectionConfig,
    battleKartSelection,
    productRenderedLocation,
    headlessCartInfo,
    headlessCart,
  } = useTrackContext();
  const [addOneMoreText, setAddOneMoreText] = useState<string>("Add One More");
  const [isItemAdded, setIsItemAdded] = useState<boolean>(false);
  const [shouldButtonBeDisabled, setShouldButtonBeDisabled] = useState<boolean>(false);

  const maxRaceQuantity = useMemo(() => {
    if (trackSelectionOption.title === "MAIN_TRACK") {
      return metaObjectMainTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity;
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      return metaObjectMiniTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity;
    } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
      return metaObjectJuniorTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity;
    } else if (trackSelectionOption.title === "VIRTUAL_REALITY") {
      return metaObjectVRSectionConfig.playersAndSessionsSection.maxSessionQuantity;
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      return metaObjectBattleKartTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity;
    }
  }, [
    trackSelectionOption.title,
    metaObjectMainTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity,
    metaObjectMiniTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity,
    metaObjectJuniorTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity,
    metaObjectVRSectionConfig.playersAndSessionsSection.maxSessionQuantity,
    metaObjectBattleKartTrackSectionConfig.seatsAndRacesSection.maxRaceQuantity,
  ]);

  const currentQuantity: number = useMemo(() => {
    if (trackSelectionOption.title === "MAIN_TRACK") {
      return mainTrackSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "MINI_TRACK") {
      return miniTrackSection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
      return juniorTrackSelection.racesSeats.numberOfSeats;
    } else if (trackSelectionOption.title === "VIRTUAL_REALITY") {
      return vrSelection.playersandsessions.numberOfPlayers;
    } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
      return battleKartSelection.racesSeats.numberOfSeats;
    }
    return 0;
  }, [
    trackSelectionOption.title,
    mainTrackSelection.racesSeats.numberOfSeats,
    miniTrackSection.racesSeats.numberOfSeats,
    juniorTrackSelection.racesSeats.numberOfSeats,
    vrSelection.playersandsessions.numberOfPlayers,
    battleKartSelection.racesSeats.numberOfSeats,
  ]);

  const handleClose = () => {
    dispatch({
      type: "SET_NOTIFICATION",
      payload: {
        ...notification,
        isVisible: false,
      },
    });
    if (shouldButtonBeDisabled) {
      setIsItemAdded(false);
      setShouldButtonBeDisabled(false);
      setAddOneMoreText("Add One More");
    }
  };

  const { isVisible } = notification;

  const isWarning: boolean = useMemo(() => {
    return notification?.title === "Warning";
  }, [notification?.title]);

  const isTooManySessions: boolean = useMemo(() => {
    return notification?.title.toLowerCase().includes("too many sessions selected");
  }, [notification?.title]);

  const isBattleKart: boolean = useMemo(() => {
    return trackSelectionOption.title === "BATTLE_KART_TRACK";
  }, [trackSelectionOption.title]);

  const handleAddOneMore = async () => {
    const variant = notification?.variant;
    if (variant) {
      if (trackSelectionOption.title === "MAIN_TRACK") {
        const isSelected = mainTrackSelection.variantsAndDate.selectedVariants.some(
          (selectedVariant: TSelectedVariants) => selectedVariant.variantsSelectedObject.id === variant.variantsSelectedObject.id
        );

        if (!isSelected) {
          const selectedVariants: TSelectedVariantAndDate = {
            ...mainTrackSelection.variantsAndDate,
            selectedVariants: [...mainTrackSelection.variantsAndDate.selectedVariants, variant],
          };
          if (maxRaceQuantity && mainTrackSelection.racesSeats.numberOfRaces < maxRaceQuantity) {
            await handleAddToCartClick(
              [{ variantSelectedTime: variant.variantSelectedTime, variantsSelectedObject: variant.variantsSelectedObject }],
              currentQuantity,
              productRenderedLocation,
              headlessCart,
              headlessCartInfo,
              dispatch
            );

            dispatch({
              type: "SET_RACES_SEATS",
              payload: {
                selectedTrack: "MAIN_TRACK",
                racesSeats: {
                  ...mainTrackSelection.racesSeats,
                  numberOfRaces: mainTrackSelection.racesSeats.numberOfRaces + 1,
                },
              },
            });

            dispatch({
              type: "SET_DATE_AND_VARIANT",
              payload: {
                variantsAndDate: selectedVariants,
              },
            });
          }
        }
      } else if (trackSelectionOption.title === "MINI_TRACK") {
        const isSelected = miniTrackSection.variantsAndDate.selectedVariants.some(
          (selectedVariant: TSelectedVariants) => selectedVariant.variantsSelectedObject.id === variant.variantsSelectedObject.id
        );

        if (!isSelected) {
          const selectedVariants: TSelectedVariantAndDate = {
            ...miniTrackSection.variantsAndDate,
            selectedVariants: [...miniTrackSection.variantsAndDate.selectedVariants, variant],
          };
          if (maxRaceQuantity && miniTrackSection.racesSeats.numberOfRaces < maxRaceQuantity) {
            await handleAddToCartClick(
              [{ variantSelectedTime: variant.variantSelectedTime, variantsSelectedObject: variant.variantsSelectedObject }],
              currentQuantity,
              productRenderedLocation,
              headlessCart,
              headlessCartInfo,
              dispatch
            );
            dispatch({
              type: "SET_RACES_SEATS_MINI_TRACK",
              payload: {
                selectedTrack: "MINI_TRACK",
                racesSeats: {
                  ...miniTrackSection.racesSeats,
                  numberOfSeats: miniTrackSection.racesSeats.numberOfSeats + 1,
                },
              },
            });

            dispatch({
              type: "SET_DATE_AND_VARIANT",
              payload: {
                variantsAndDate: selectedVariants,
              },
            });
          }
        }
      } else if (trackSelectionOption.title === "VIRTUAL_REALITY") {
        const isSelected = vrSelection.joinedVariants.some((selectedVariant: IGVariant | TGameCustomization) => {
          if ("id" in selectedVariant) {
            return selectedVariant.id === variant.variantsSelectedObject.id;
          } else {
            return selectedVariant.selectedVariant.id === variant.variantsSelectedObject.id;
          }
        });

        if (!isSelected) {
          const addSelectedVariants = {
            ...vrSelection.variantsAndDate,
            selectedVariants: [...vrSelection.variantsAndDate.selectedVariants, variant],
          };

          if (maxRaceQuantity && vrSelection.playersandsessions.numberOfSessions < maxRaceQuantity) {
            await handleAddToCartClick(
              [{ variantSelectedTime: variant.variantSelectedTime, variantsSelectedObject: variant.variantsSelectedObject }],
              currentQuantity,
              productRenderedLocation,
              headlessCart,
              headlessCartInfo,
              dispatch
            );
            dispatch({
              type: "SET_PLAYERS_SESSIONS_VIRTUAL_REALITY",
              payload: {
                playersAndSessions: {
                  ...vrSelection.playersandsessions,
                  numberOfSessions: vrSelection.playersandsessions.numberOfSessions + 1,
                },
              },
            });

            dispatch({
              type: "SET_DATE_AND_VARIANT_VIRTUAL_REALITY",
              payload: {
                variantsAndDate: addSelectedVariants,
              },
            });
          }
        }
      } else if (trackSelectionOption.title === "JUNIOR_TRACK") {
        const isSelected = juniorTrackSelection.variantsAndDate.selectedVariants.some(
          (selectedVariant: TSelectedVariants) => selectedVariant.variantsSelectedObject.id === variant.variantsSelectedObject.id
        );

        if (!isSelected) {
          const selectedVariants: TSelectedVariantAndDate = {
            ...juniorTrackSelection.variantsAndDate,
            selectedVariants: [...juniorTrackSelection.variantsAndDate.selectedVariants, variant],
          };

          if (maxRaceQuantity && juniorTrackSelection.racesSeats.numberOfRaces < maxRaceQuantity) {
            await handleAddToCartClick(
              [{ variantSelectedTime: variant.variantSelectedTime, variantsSelectedObject: variant.variantsSelectedObject }],
              currentQuantity,
              productRenderedLocation,
              headlessCart,
              headlessCartInfo,
              dispatch
            );
            dispatch({
              type: "SET_RACES_SEATS_JUNIOR_TRACK",
              payload: {
                racesSeats: {
                  ...juniorTrackSelection.racesSeats,
                  numberOfRaces: juniorTrackSelection.racesSeats.numberOfRaces + 1,
                },
              },
            });

            dispatch({
              type: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
              payload: {
                variantsAndDate: selectedVariants,
              },
            });
          }
        }
      } else if (trackSelectionOption.title === "BATTLE_KART_TRACK") {
        const isSelected = battleKartSelection.variantsAndDate.selectedVariants.some(
          (selectedVariant: TSelectedVariants) => selectedVariant.variantsSelectedObject.id === variant.variantsSelectedObject.id
        );

        if (!isSelected) {
          const selectedVariants: TSelectedVariantAndDate = {
            ...battleKartSelection.variantsAndDate,
            selectedVariants: [...battleKartSelection.variantsAndDate.selectedVariants, variant],
          };

          if (maxRaceQuantity && battleKartSelection.racesSeats.numberOfRaces < maxRaceQuantity) {
            await handleAddToCartClick(
              [{ variantSelectedTime: variant.variantSelectedTime, variantsSelectedObject: variant.variantsSelectedObject }],
              currentQuantity,
              productRenderedLocation,
              headlessCart,
              headlessCartInfo,
              dispatch
            );
            dispatch({
              type: "SET_RACES_SEATS_BATTLE_KART_TRACK",
              payload: {
                racesSeats: {
                  ...battleKartSelection.racesSeats,
                  numberOfRaces: battleKartSelection.racesSeats.numberOfRaces + 1,
                },
              },
            });

            dispatch({
              type: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
              payload: {
                variantsAndDate: selectedVariants,
              },
            });
          }
        }
      }
    }

    setAddOneMoreText("Loading...");

    setTimeout(() => {
      setAddOneMoreText("Add One More");
      setShouldButtonBeDisabled(true);
      setIsItemAdded(true);
    }, 1000);
  };

  return (
    <>
      {isVisible && (
        <div className="tw-fixed tw-z-[1000] tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-gray-900 tw-bg-opacity-50">
          <div className="tw-mx-[20px] lg:tw-mx-0">
            <div className="tw-bg-[#393B41] lg:tw-max-w-[998px] tw-w-full tw-mx-auto tw-rounded-[16px] tw-shadow-lg tw-overflow-hidden tw-transform tw-transition-all tw-duration-300 tw-scale-100">
              <div className="tw-py-[10px] tw-px-[10px]">
                <div className="tw-flex tw-justify-end">
                  <button className="tw-text-gray-600 tw-text-lg tw-p-2 tw-hover:text-gray-800 tw-focus:tw-outline-none" onClick={handleClose}>
                    <AiOutlineClose className="tw-w-[30px] tw-h-[30px] tw-text-white tw-font-[400]" />
                  </button>
                </div>
                <div className="tw-flex tw-justify-center tw-items-center">
                  <div
                    className={`tw-text-white tw-text-center tw-p-0 ${isWarning ? "lg:tw-py-[40px]" : isTooManySessions ? "lg:tw-py-[20px]" : "lg:tw-py-[80px]"} ${
                      isWarning ? "lg:tw-px-[30px]" : isTooManySessions ? "lg:tw-px-[120px]" : "lg:tw-px-[180px]"
                    }`}
                  >
                    {!isTooManySessions && <span className="tw-font-[700] tw-text-[30px] lg:tw-text-[36px] tw-mb-[20px] tw-text-white tw-block">{notification?.title}</span>}
                    <p className="tw-text-[20px] lg:tw-text-[30px] tw-text-[#EBECEF] tw-px-[10px] lg:tw-px-0">{notification?.content}</p>
                    {isTooManySessions && !isItemAdded && (
                      <p className="tw-font-[700] tw-text-[30px] lg:tw-text-[30px] tw-mb-[30px] tw-mt-[20px] tw-text-white tw-block">Do you want to add another session?</p>
                    )}
                    {isItemAdded && !isWarning && (
                      <p className="tw-font-[700] tw-text-[20px] tw-mb-[20px] tw-text-green-500 tw-block">
                        Your selected session has been added successfully{" "}
                        <AiOutlineCheckCircle className="tw-w-[30px] tw-h-[30px] tw-text-green-500 tw-font-[400] tw-inline-block tw-ml-[10px]" />
                      </p>
                    )}
                    <div className="tw-flex tw-justify-center tw-items-center tw-gap-[20px]">
                      {isTooManySessions && (
                        <button
                          onClick={handleAddOneMore}
                          className={`tw-w-fit tw-h-[40px] lg:tw-h-[57px] tw-overflow-hidden tw-py-[10px] lg:tw-py-[16px] tw-px-[20px] lg:tw-px-[40px] tw-font-bold tw-text-[12px] lg:tw-text-[18px] tw-rounded-[100px] tw-text-white ${
                            isBattleKart 
                              ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)] add-grobold-font" 
                              : "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]"
                          } ${shouldButtonBeDisabled ? "tw-opacity-50" : ""}`}
                          disabled={shouldButtonBeDisabled}
                        >
                          {addOneMoreText}
                        </button>
                      )}
                      <button
                        onClick={handleClose}
                        className={`tw-w-fit tw-h-[40px] lg:tw-h-[57px] tw-overflow-hidden tw-py-[10px] lg:tw-py-[16px] tw-px-[20px] lg:tw-px-[40px] tw-font-bold tw-text-[12px] lg:tw-text-[18px] tw-rounded-[100px] tw-text-white ${
                          isBattleKart 
                            ? "tw-bg-[linear-gradient(180deg,#FAC618_0%,#EA581A_100%)] add-grobold-font" 
                            : "tw-bg-[linear-gradient(71deg,#a852ff_28.13%,#7886ff)]"
                        }`}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notification;
